import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { ResourceType, type ResourceUpdatedPayload } from '@cotera/api';
import { queryKeys } from './query-cache-keys';

export type ResourceUpdatedWireType = ResourceUpdatedPayload['resourceType'];

/**
 * Cached-query invalidation tied to websocket `resource-updated` by wire discriminator.
 *
 * Adding a variant to `ResourceUpdatedPayload` must add exactly one matching entry here or
 * TypeScript will complain (`Record` completeness). Typical fields:
 *
 * - `listPages` — prefixes `[orgId, 'resource', 'listPage', …]` and
 *   `[orgId, 'resource', 'list', …]` invalidated for UI lists and hooks like
 *   `useCocoMentions` / `useAgentMentions` that cache `resource.list` slices.
 * - `resourceEnumForDetail` — invalidates `queryKeys.resource.get` when payload has `resourceId`
 *   (`null` for folder; folder-specific GET/descendants live in {@link ResourceUpdatedRule.extraPrefixes}).
 * - `invalidateAgentsShellDirectory` — top-bar eager `agents.all` slice.
 * - `extraPrefixes` — anything else for this wire type (sidebar folder tree, etc.).
 */
export type ResourceUpdatedRule = Readonly<{
  listPages: readonly ResourceType[];
  resourceEnumForDetail: ResourceType | null;
  invalidateAgentsShellDirectory?: boolean;
  extraPrefixes?: (
    orgId: string,
    payload: ResourceUpdatedPayload
  ) => readonly QueryKey[];
}>;

/** Single place to declare ResourceUpdated-driven React Query staleness rules. */
export const RESOURCE_UPDATED_CACHE_RULES: Record<
  ResourceUpdatedWireType,
  ResourceUpdatedRule
> = {
  chat: {
    listPages: [ResourceType.Chat],
    resourceEnumForDetail: ResourceType.Chat,
  },
  agent: {
    listPages: [ResourceType.Agent],
    resourceEnumForDetail: ResourceType.Agent,
    invalidateAgentsShellDirectory: true,
  },
  folder: {
    listPages: [ResourceType.Folder],
    resourceEnumForDetail: null,
    extraPrefixes(orgId, payload) {
      if (payload.resourceType !== 'folder') {
        return [];
      }
      const keys: QueryKey[] = [
        queryKeys.resource.folderTree({ orgId }),
        [orgId, 'resource', 'folderList'],
      ];

      const resourceId =
        typeof payload.resourceId === 'string' && payload.resourceId !== ''
          ? payload.resourceId
          : undefined;
      if (resourceId !== undefined) {
        keys.push(
          queryKeys.resource.get({
            orgId,
            type: ResourceType.Folder,
            id: resourceId,
          }),
          queryKeys.resource.folderDescendantIds({
            orgId,
            folderId: resourceId,
          })
        );
      }
      return keys;
    },
  },
  document: {
    listPages: [ResourceType.Document],
    resourceEnumForDetail: ResourceType.Document,
  },
  dataset: {
    listPages: [ResourceType.Dataset],
    resourceEnumForDetail: ResourceType.Dataset,
  },
  workflow: {
    listPages: [ResourceType.Workflow, ResourceType.WorkflowTrigger],
    resourceEnumForDetail: ResourceType.Workflow,
    extraPrefixes(orgId, payload) {
      if (payload.resourceType !== 'workflow') {
        return [];
      }
      const workflowId =
        typeof payload.resourceId === 'string' && payload.resourceId !== ''
          ? payload.resourceId
          : undefined;
      if (workflowId === undefined) {
        return [];
      }
      // Run status broadcasts arrive as workflow ResourceUpdated events;
      // refresh the per-run and runs-list caches (e.g. the chat workflow
      // tool card's live run status).
      return [
        [orgId, 'workflow', 'run', workflowId],
        [orgId, 'workflow', 'runs', workflowId],
      ];
    },
  },
  workflow_trigger: {
    listPages: [ResourceType.WorkflowTrigger, ResourceType.Workflow],
    resourceEnumForDetail: ResourceType.WorkflowTrigger,
    extraPrefixes(orgId, payload) {
      if (payload.resourceType !== 'workflow_trigger') {
        return [];
      }
      const triggerId =
        typeof payload.resourceId === 'string' && payload.resourceId !== ''
          ? payload.resourceId
          : undefined;
      if (triggerId === undefined) {
        return [];
      }
      return [
        queryKeys.webhookTriggers.settings({ orgId, triggerId }),
        [orgId, 'webhookTriggers', 'settings'],
      ];
    },
  },
};

/**
 * A mid-stream chunk notify: the chat's *messages* changed, nothing about where
 * it sits or which lists it belongs to. These arrive per streamed message part,
 * so treating one as a list change makes every chat list refetch at token rate.
 */
export function isStreamingChatUpdate(
  payload: ResourceUpdatedPayload
): boolean {
  return payload.resourceType === 'chat' && payload.streaming === true;
}

export function prefixesToInvalidateForResourceUpdated(
  orgId: string,
  payload: ResourceUpdatedPayload
): QueryKey[] {
  const rule = RESOURCE_UPDATED_CACHE_RULES[payload.resourceType];

  const keys: QueryKey[] = [];

  // A streaming chunk still refreshes the open chat below (`resource.get`), but
  // its position in every list is unchanged, so the list slices stay valid.
  if (!isStreamingChatUpdate(payload)) {
    for (const rt of rule.listPages) {
      keys.push([orgId, 'resource', 'listPage', rt]);
      keys.push([orgId, 'resource', 'list', rt]);
    }
  }

  const resourceEnum = rule.resourceEnumForDetail;

  const resourceId =
    'resourceId' in payload &&
    typeof payload.resourceId === 'string' &&
    payload.resourceId !== ''
      ? payload.resourceId
      : undefined;

  if (resourceEnum !== null && resourceId !== undefined) {
    keys.push(
      queryKeys.resource.get({
        orgId,
        type: resourceEnum,
        id: resourceId,
      })
    );
  }

  if (rule.invalidateAgentsShellDirectory === true) {
    keys.push(queryKeys.agents.all({ orgId }));
  }

  if (rule.extraPrefixes !== undefined) {
    keys.push(...rule.extraPrefixes(orgId, payload));
  }

  return keys;
}

function isResourceUpdatedPayload(
  payload: unknown
): payload is ResourceUpdatedPayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'orgId' in payload &&
    'resourceType' in payload &&
    typeof (payload as { orgId?: unknown }).orgId === 'string'
  );
}

function isCreditBalancePayload(
  payload: unknown
): payload is { orgId: string } {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'orgId' in payload &&
    typeof (payload as { orgId?: unknown }).orgId === 'string'
  );
}

function isToolCredentialUpdatedPayload(
  payload: unknown
): payload is { orgId: string; provider?: string } {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'orgId' in payload &&
    typeof (payload as { orgId?: unknown }).orgId === 'string'
  );
}

async function invalidateAllFolderContentsAndUnfiled(
  queryClient: QueryClient,
  orgId: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [orgId, 'resource', 'folderContents'],
    }),
    queryClient.invalidateQueries({
      queryKey: [orgId, 'resource', 'libraryRoot'],
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.resource.unfiledTotals({ orgId }),
    }),
  ]);
}

/**
 * Drops TanStack entries for org-wide movers (folder placements) plus type-specific prefixes
 * from {@link RESOURCE_UPDATED_CACHE_RULES}.
 */
export async function invalidateQueriesForResourceUpdated(
  queryClient: QueryClient,
  orgId: string,
  payloadUnknown: unknown
): Promise<void> {
  if (!isResourceUpdatedPayload(payloadUnknown)) {
    return;
  }
  const payload = payloadUnknown;
  if (payload.orgId !== orgId) {
    return;
  }

  const promises: Promise<unknown>[] = [];

  // Folder placement, library root and unfiled totals cannot change while a
  // message streams, so a chunk skips the org-wide sweep too.
  if (!isStreamingChatUpdate(payload)) {
    promises.push(invalidateAllFolderContentsAndUnfiled(queryClient, orgId));
  }

  for (const queryKey of prefixesToInvalidateForResourceUpdated(
    orgId,
    payload
  )) {
    promises.push(queryClient.invalidateQueries({ queryKey }));
  }

  await Promise.all(promises);
}

export async function invalidateQueriesForRecentChatsUpdated(
  queryClient: QueryClient,
  orgId: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [orgId, 'resource', 'listPage', ResourceType.Chat],
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.chat.conversations({ orgId }),
    }),
    invalidateAllFolderContentsAndUnfiled(queryClient, orgId),
  ]);
}

export async function invalidateQueriesForCreditBalanceUpdated(
  queryClient: QueryClient,
  orgId: string,
  payloadUnknown: unknown
): Promise<void> {
  if (!isCreditBalancePayload(payloadUnknown)) {
    return;
  }
  if (payloadUnknown.orgId !== orgId) {
    return;
  }

  await queryClient.invalidateQueries({
    queryKey: queryKeys.settings.billing.credit_balance(orgId),
  });
}

/**
 * Refreshes all tool caches on a `tool-credential-updated` broadcast. Every
 * `toolsV2` query key shares the `[orgId, 'toolsV2']` prefix, so a single
 * prefix invalidation refreshes the tool list, providers, provider accounts,
 * and credential info — propagating into hooks like `useToolsAsync` /
 * `useCocoMentions`.
 */
export async function invalidateQueriesForToolCredentialUpdated(
  queryClient: QueryClient,
  orgId: string,
  payloadUnknown: unknown
): Promise<void> {
  if (!isToolCredentialUpdatedPayload(payloadUnknown)) {
    return;
  }
  if (payloadUnknown.orgId !== orgId) {
    return;
  }

  await queryClient.invalidateQueries({
    queryKey: queryKeys.toolsV2.all({ orgId }),
  });
}
